import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';

const UserContext = createContext();

/** Convierte el array de permisos en un mapa { modulo: { ver, crear, editar, eliminar } } */
function normalizarPermisos(permisosArray = []) {
  const arr = Array.isArray(permisosArray?.$values)
    ? permisosArray.$values
    : Array.isArray(permisosArray) ? permisosArray : [];

  return arr.reduce((acc, p) => {
    acc[p.modulo] = {
      ver:      p.puedeVer,
      crear:    p.puedeCrear,
      editar:   p.puedeEditar,
      eliminar: p.puedeEliminar,
    };
    return acc;
  }, {});
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Al cargar la app verifica si hay sesión activa
  useEffect(() => {
    api.get('/Usuario/me')
      .then(({ data }) => setUser({
        usuarioId:    data.usuarioId,
        nombreUsuario: data.nombreUsuario,
        nombre:        data.nombre,
        rolId:         data.rolId,
        rolNombre:     data.rolNombre,
        esAdmin:       data.esAdmin ?? false,
        permisos:      normalizarPermisos(data.permisos),
      }))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((data) => {
    const userData = {
      usuarioId:     data.usuarioId,
      nombreUsuario: data.nombreUsuario,
      nombre:        data.nombre,
      rolId:         data.rolId,
      rolNombre:     data.rolNombre,
      esAdmin:       data.esAdmin ?? false,
      permisos:      normalizarPermisos(data.permisos),
    };
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/Usuario/logout'); } catch {}
    setUser(null);
  }, []);

  return (
    <UserContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

/**
 * Hook para verificar permisos del usuario logueado.
 * Uso: const { puedo } = usePermisos();
 *      puedo('leche', 'ver')    → true/false
 *      puedo('usuarios', 'crear') → true/false
 */
export function usePermisos() {
  const { user } = useUser();

  const puedo = useCallback((modulo, accion = 'ver') => {
    if (!user) return false;
    if (user.esAdmin) return true;  // admin ve todo
    return user.permisos?.[modulo]?.[accion] === true;
  }, [user]);

  const puedeVer      = (modulo) => puedo(modulo, 'ver');
  const puedeCrear    = (modulo) => puedo(modulo, 'crear');
  const puedeEditar   = (modulo) => puedo(modulo, 'editar');
  const puedeEliminar = (modulo) => puedo(modulo, 'eliminar');

  return { puedo, puedeVer, puedeCrear, puedeEditar, puedeEliminar };
}
